// Network Types
export type NetworkType = 'bitcoin' | 'ethereum' | 'ton' | 'tron' | 'solana' | 'spark';

// Wallet Configuration
export interface ZubariWalletConfig {
  network: 'mainnet' | 'testnet';
  enabledNetworks: NetworkType[];
  gasless: boolean;
  paymasterUrl?: string;
  bundlerUrl?: string;
  rpcUrls?: Partial<Record<NetworkType, string>>;
}

// Balance Types
export interface TokenBalance {
  symbol: string;
  address: string;
  balance: bigint;
  balanceFormatted: string;
  balanceUsd: number;
  decimals: number;
}

export interface NetworkBalance {
  network: NetworkType;
  native: {
    symbol: string;
    balance: bigint;
    balanceFormatted: string;
    balanceUsd: number;
  };
  tokens: TokenBalance[];
}

// Transaction Types
export interface TxResult {
  hash: string;
  network: NetworkType;
  status: 'pending' | 'confirmed' | 'failed';
  blockNumber?: number;
  gasUsed?: bigint;
  error?: string;
  metadata?: Record<string, unknown>;
}

export interface SendParams {
  to: string;
  amount: bigint;
  token?: string;
  gasless?: boolean;
}

// Account Types
export interface Account {
  network: NetworkType;
  address: string;
  publicKey: string;
  derivationPath: string;
}

// NFT Types
export interface NFTMetadata {
  name: string;
  description: string;
  image: string;
  attributes?: Array<{ trait_type: string; value: string | number }>;
  royaltyBps: number;
}

export interface LazyMintVoucher {
  tokenId: string;
  uri: string;
  creator: string;
  royaltyBps: number;
  deadline: number;
  signature: string;
}

export interface NFT {
  tokenId: string;
  contractAddress: string;
  owner: string;
  creator: string;
  uri: string;
  metadata?: NFTMetadata;
  isLazyMinted: boolean;
}

export interface ListingParams {
  tokenId: string;
  price: bigint;
  paymentToken: string;
  duration: number;
}

// Tip Types
export interface TipData {
  recipient: string;
  amount: bigint;
  token: string;
  message?: string;
}

export interface TipResult {
  txHash: string;
  tipId: string;
  recipient: string;
  amount: bigint;
  platformFee: bigint;
  timestamp: number;
}

export interface TipStats {
  totalReceived: bigint;
  tipCount: number;
  uniqueTippers: number;
}

// Subscription Types
export interface SubscriptionPlan {
  planId?: string;
  name: string;
  description: string;
  price: bigint;
  paymentToken: string;
  duration: number;
  perks: string[];
  maxSubscribers?: number;
  nftBadge: boolean;
}

export interface Subscription {
  subscriptionId: string;
  planId: string;
  creator: string;
  subscriber: string;
  startTime: number;
  endTime: number;
  autoRenew: boolean;
  status: 'active' | 'expired' | 'cancelled';
}

// Payout Types
export interface EarningsBreakdown {
  tips: bigint;
  subscriptions: bigint;
  nftSales: bigint;
  royalties: bigint;
  total: bigint;
}

export interface RevenueSplit {
  recipient: string;
  basisPoints: number;
}

// Swap Types
export interface SwapQuote {
  tokenIn: string;
  tokenOut: string;
  amountIn: bigint;
  amountOut: bigint;
  priceImpact: number;
  route: string[];
  estimatedGas: bigint;
}

export interface SwapParams {
  tokenIn: string;
  tokenOut: string;
  amountIn: bigint;
  minAmountOut: bigint;
  deadline: number;
}

// Error Types
export type ZubariErrorCode =
  | 'TX_FAILED'
  | 'AUTH_EXPIRED'
  | 'RPC_ERROR'
  | 'VOUCHER_EXPIRED'
  | 'SLIPPAGE_ERROR'
  | 'RATE_LIMITED'
  | 'UPLOAD_ERROR'
  | 'INSUFFICIENT_FUNDS'
  | 'INVALID_SIGNATURE'
  | 'UNSUPPORTED_NETWORK'
  | 'UNKNOWN_ERROR';

export class ZubariError extends Error {
  constructor(
    public readonly code: ZubariErrorCode,
    message: string,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = 'ZubariError';
  }
}
